import { useContextSelector } from '@fluentui/react-context-selector';
import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { ArrowForwardIos as ForwardIcon } from '@material-ui/icons';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import {
  Activity,
  Booking,
  CreditCard,
  HELP_URL,
  Payline,
  PaymentProvider,
  helpers,
} from '@aps-management/primapp-common';

import Payment from '_components/Payment';
import { Screen } from '_components/core';
import { DialogPaymentIP } from '_components/elements';
import apolloClient from '_utils/apolloClient';

/* */
import i18n from '_utils/i18n';
import masterClient from '_utils/masterClient';
import { assertCaptured, buildTransactionFields } from '_utils/paymentGuard';

import ActivityContext from './contexts/ActivityContext';

const styles = (theme) => ({
  root: {
    padding: theme.spacing.unit * 3,
    maxWidth: 600,
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing.unit * 3,
  },
  icon: {
    color: theme.palette.text.primary,
    fontSize: 40,
    marginRight: theme.spacing.unit * 2,
  },
  title: {
    fontWeight: 600,
  },
  section: {
    marginBottom: theme.spacing.unit * 3,
  },
  divider: {
    margin: theme.spacing.unit * 2,
  },
  paymentMethod: {
    marginBottom: theme.spacing.unit * 2,
  },
  actions: {
    marginTop: theme.spacing.unit * 4,
    display: 'flex',
    justifyContent: 'space-between',
  },
  label: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  value: {
    fontWeight: 400,
  },
});

const loadPaylineScript = () =>
  new Promise((resolve, reject) => {
    if (window.Payline?.Api) {
      resolve();
      return;
    }

    const existingScript = document.getElementById('payline-widget-script');
    if (existingScript) {
      existingScript.addEventListener('load', resolve);
      existingScript.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.id = 'payline-widget-script';
    script.src = 'https://payment.cdn.payline.com/cdn/scripts/widget-min.js';
    script.async = true;
    script.addEventListener('load', resolve);
    script.addEventListener('error', reject);
    document.body.appendChild(script);
  });

const ActivityPayment = ({ account, golf }) => {
  const history = useHistory();
  const location = useLocation();
  const { error, setError, load, isPublic } = useContextSelector(ActivityContext, (c) => c);
  const [waiting, setWaiting] = useState(false); // Add a state for waiting
  const [imprint, setImprint] = useState(null); // Add a state for imprint
  const [status, setStatus] = useState(null); // Add a state for status
  const paylineWidgetData = useRef(null);
  const isPaylinePaymentHandled = useRef(false);
  const removePaylineListeners = useRef(null);
  const { selectedSlot, bookingInfo, players } = location.state || {
    selectedSlot: 'Non spécifié',
    bookingInfo: null,
    players: [],
  };

  const removePaylineWidget = () => {
    const widgetContainer = document.getElementById('PaylineWidget');

    if (widgetContainer && widgetContainer.children.length > 0) {
      try {
        if (window.Payline?.Api?.hide) {
          const hideRes = window.Payline.Api.hide();
          if (hideRes && hideRes.catch) {
            hideRes.catch((e) => console.warn('Payline hide promise failed', e));
          }
        }
      } catch (e) {
        console.warn('Payline hide failed', e);
      }
    }

    if (widgetContainer) {
      widgetContainer.removeAttribute('data-token');
      widgetContainer.innerHTML = '';
    }

    if (removePaylineListeners.current) {
      removePaylineListeners.current();
    }
  };

  const handlePaylinePaymentError = (eventData = {}, defaultMessage = 'Le paiement a échoué. Veuillez réessayer.') => {
    removePaylineWidget();

    setWaiting(false);
    setStatus(null);
    setError(eventData?.message || defaultMessage);
    isPaylinePaymentHandled.current = false;
  };

  const handlePaylinePaymentSuccess = (eventData = {}) => {
    if (isPaylinePaymentHandled.current || !paylineWidgetData.current) {
      return;
    }

    isPaylinePaymentHandled.current = true;

    const data = paylineWidgetData.current;
    const token = eventData?.token || data.token;
    const saveImprint = sessionStorage.getItem('payline_saveImprint') === '1';

    /*
     * L'argent est en jeu À PARTIR D'ICI, pas à partir du moment où nous parvenons à
     * construire `data.transaction`.
     *
     * Le widget ne signale un succès qu'après le débit. Tout ce qui suit - la
     * vérification serveur, le verdict de Monext, la confirmation - peut échouer
     * APRÈS. Faire dépendre la reprise de `data.transaction`, construit bien plus
     * bas, revenait à sauter le filet exactement dans le cas pour lequel il existe :
     * la réponse qui se perd. Et à afficher « réessayez » à un golfeur déjà débité,
     * c'est-à-dire à l'inviter à payer une seconde fois, sur un tunnel où
     * `activityCancel` ne rembourse pas.
     *
     * C'est donc ce drapeau, et lui seul, qui dit à `recoverAfterCapture` qu'il y a
     * de l'argent à protéger.
     */
    data.widgetCaptured = true;

    removePaylineWidget();

    setError(null);
    setWaiting(true);
    setStatus('Vérification du paiement...');

    /*
     * On ENREGISTRE l'encaissement côté serveur, rattaché à la commande, au lieu de
     * seulement le LIRE.
     *
     * `checkPayment` interrogeait Monext et n'écrivait rien : le débit n'existait
     * nulle part dans `sales_order_payment`, la commande d'activité restait `booked`
     * sans aucun paiement rattaché, et aucun filet serveur ne pouvait la rattraper -
     * `bookingConfirmIfCaptured` cherche d'abord un paiement RATTACHÉ à la commande,
     * et son repli par session ne reconnaît que les références `BKG-`, jamais les
     * `ACT-` ouvertes ici. Autrement dit : golfeur débité, activité non réservée,
     * et rien à quoi se raccrocher pour la récupérer ou la rembourser.
     *
     * `recordWidgetPayment` est exactement le résolveur utilisé par le tunnel
     * green-fee depuis l'incident LAZZARETTO du 19/07. Il est agnostique du tunnel :
     * il ne contrôle que le propriétaire de la commande et l'égalité stricte du
     * montant encaissé, il est idempotent sur le numéro de transaction, et il ne
     * fait jamais échouer le flux.
     *
     * L'autre branche, `captureWithToken`, reste inchangée : elle encaisse sous
     * `reference: data.id`, c'est-à-dire l'identifiant de la commande, ce qui la rend
     * retrouvable par la recherche Monext de `bookingConfirmIfCaptured`. Les deux
     * branches sont donc rattrapables, par deux chemins différents.
     *
     * Les deux ne renvoient PAS les mêmes champs : `recordWidgetPayment` expose
     * `status` et `code`, `captureWithToken` expose `resultCode` et pas `status`. Le
     * contrôle ci-dessous lit les deux, ne pas le « simplifier ».
     */
    const processPayment = saveImprint
      ? Payline.api.captureWithToken(apolloClient, {
          token,
          amount: data.amount,
          uuid: data.id,
          reference: data.id,
          customer: {
            firstName: account.firstname,
            lastName: account.lastname,
            email: account.email,
          },
        })
      : Payline.api.recordWidgetPayment(apolloClient, { orderId: data.id, amount: data.amount, token });

    return processPayment
      .then((result) => {
        if (!result) {
          throw new Error('Impossible de vérifier le paiement Payline.');
        }

        /*
         * Le widget a signalé un succès, mais c'est le verdict de Monext relu côté
         * serveur qui fait foi. Sans ce contrôle, une activité était confirmée sur un
         * paiement refusé.
         *
         * Le contrôle est écrit BRANCHE PAR BRANCHE, et ce n'est pas de la
         * verbosité : les deux appels ne rendent pas le verdict dans le même champ.
         * `recordWidgetPayment` rend `status` ('success' ou 'failed') et `code` ;
         * `captureWithToken` rend `resultCode` et pas de `status`. Un contrôle unique
         * lisant `resultCode` était inerte sur la branche widget - le champ y est
         * toujours absent - et une tolérance écrite dessus n'aurait rien toléré du
         * tout. Ne pas fusionner les deux.
         */
        if (saveImprint) {
          if (result.resultCode !== '00000' && result.resultCode !== '02500') {
            throw new Error(`Le paiement a échoué: ${result.resultCode || 'Erreur inconnue'}`);
          }
        } else if (result.status !== 'success') {
          /*
           * `02500` (« en cours de traitement ») n'est volontairement PAS toléré ici,
           * contrairement à la branche ci-dessus : sur ce code, master refuse
           * d'enregistrer le paiement. Confirmer l'activité reviendrait à livrer sur
           * un encaissement dont il n'existe aucune trace en base. On part donc en
           * reprise, qui ne perd pas l'argent et ne pousse pas à repayer.
           */
          throw new Error(`Le paiement a échoué: ${result.code || result.status || 'Erreur inconnue'}`);
        }

        data.transaction = {
          ...data.transaction,
          amount: data.amount,
          provider: 'payline',
          callNo: data.transaction?.callNo || eventData?.authorizationNumber || result.authorizationNumber,
          transactionNo: data.transaction?.transactionNo || eventData?.transactionId || result.transactionId,
          card: result.maskedCardNumber || data.transaction?.card,
          brand: data.transaction?.brand || result.cardBrand,
          country: data.transaction?.country || result.cardCountry,
        };

        if (saveImprint) {
          setStatus('Enregistrement de la carte...');
          /*
           * La carte est créée ici, dans la même opération que le paiement. On mémorise
           * son identifiant et on le reporte dans la transaction : sinon `bookingConfirm`
           * écrivait `credit_card_id = NULL`, le lien n'étant posé que pour une carte
           * déjà enregistrée.
           */
          const newCreditCardId = helpers.uuid.createUuid();
          data.transaction.creditCardId = newCreditCardId;

          return CreditCard.api
            .setCreditCard(apolloClient, {
              id: newCreditCardId,
              token: result.token || token,
              number: result.maskedCardNumber,
              name: 'Payline',
              dueDate: result.expiryDate ? result.expiryDate : result.expirationDate ? result.expirationDate : '0000',
              brand: result.cardBrand,
              country: result.cardCountry,
              walletId: result.walletId,
              paymentRecordId: result.transactionId,
            })
            .then(() => {
              sessionStorage.removeItem('payline_saveImprint');
              return CreditCard.api
                .getCreditCards(apolloClient, { accountId: account.id })
                .then(({ creditCards }) => {
                  if (creditCards && creditCards.length > 0) {
                    setImprint(creditCards[0]);
                  }
                })
                .catch(() => {});
            })
            .then(() => {
              setStatus('Nous confirmons votre paiement...');
              return Booking.api.bookingConfirm(apolloClient, {
                id: data.id,
                transaction: data.transaction,
              });
            });
        }

        sessionStorage.removeItem('payline_saveImprint');
        setStatus('Nous confirmons votre paiement...');

        return Booking.api.bookingConfirm(apolloClient, {
          id: data.id,
          transaction: data.transaction,
        });
      })
      /*
       * Même filet que les deux autres moyens de paiement de cet écran : après un
       * encaissement abouti, on tente la reprise serveur plutôt que d'afficher une
       * erreur sèche. On n'annule JAMAIS ici : `activityCancel` libère le créneau sans
       * rembourser, il ferait donc perdre l'argent au golfeur.
       */
      .catch(recoverAfterCapture(data))
      .then(({ transaction }) => {
        history.push({
          pathname: '/activity/confirmation',
          state: {
            ...data.locationState,
            ...data,
            transaction,
          },
        });
      })
      .catch((err) => {
        isPaylinePaymentHandled.current = false;
        if (!err.handled) {
          setWaiting(false);
          setStatus(null);
          setError(err.message);
        }
        removePaylineWidget();
      });
  };

  const setupPaylineListeners = () => {
    const api = window.Payline?.Api;
    const listeners = [];

    const bindApiEvent = (eventName, handler) => {
      if (!api?.on || !eventName) return;

      api.on(eventName, handler);

      listeners.push(() => {
        if (api?.off) {
          api.off(eventName, handler);
        }
      });
    };

    const bindWindowEvent = (eventName, handler) => {
      window.addEventListener(eventName, handler);
      listeners.push(() => {
        window.removeEventListener(eventName, handler);
      });
    };

    const onPaymentSuccess = (eventData) => {
      handlePaylinePaymentSuccess(eventData);
    };

    const onPaymentFailure = (eventData) => {
      handlePaylinePaymentError(eventData, 'Le paiement a échoué. Veuillez réessayer.');
    };

    const onPaymentCancel = (eventData) => {
      handlePaylinePaymentError(eventData, 'Le paiement a été annulé.');
    };

    bindApiEvent(api?.EVENT?.PAYMENT_SUCCESS, onPaymentSuccess);
    bindApiEvent(api?.EVENT?.PAYMENT_FAILURE, onPaymentFailure);
    bindApiEvent(api?.EVENT?.PAYMENT_CANCELED, onPaymentCancel);
    bindApiEvent(api?.EVENT?.PAYMENT_CANCEL, onPaymentCancel);
    bindWindowEvent('payline-payment-success', onPaymentSuccess);
    bindWindowEvent('payline-payment-failure', onPaymentFailure);
    bindWindowEvent('payline-payment-cancel', onPaymentCancel);

    removePaylineListeners.current = () => {
      listeners.forEach((removeListener) => removeListener());
      removePaylineListeners.current = null;
    };
  };

  const showPaylineWidget = (token) => {
    removePaylineWidget();

    const widgetContainer = document.getElementById('PaylineWidget');
    if (!widgetContainer) {
      throw new Error('Impossible d’afficher le widget de paiement.');
    }

    widgetContainer.setAttribute('data-token', token);
    widgetContainer.setAttribute('data-template', 'column');
    widgetContainer.setAttribute('data-embeddedredirectionallowed', 'false');
    widgetContainer.setAttribute('data-auto-init', 'false');

    setupPaylineListeners();

    try {
      if (window.Payline?.Api?.reset) {
        window.Payline.Api.reset(token, 'column');
      } else {
        if (window.Payline?.Api?.init) {
          window.Payline.Api.init();
        }
        window.Payline.Api.show();
      }
    } catch (e) {
      console.error('Payline init/reset crashed:', e);
      throw e;
    }
  };

  useEffect(() => {
    CreditCard.api.getCreditCards(apolloClient, {}).then(({ creditCards }) => {
      // Garde-fou : seule une carte disposant d'un wallet Payline réel (walletId) peut
      // être débitée en 1 clic. Une carte sans walletId (enregistrée avant le correctif
      // ou via un chemin legacy) produirait subscriber.id=null : mutation rejetée. On ne
      // la propose donc pas : l'utilisateur ressaisit sa carte (le widget crée un vrai
      // wallet), ce qui répare la situation de lui-même.
      const usable = (creditCards || []).find((c) => c && c.walletId);
      if (usable) {
        const newStatus = CreditCard.functions.getStatus(usable.dueDate);
        setImprint({
          ...newStatus,
          ...usable,
        });
      }
    });
  }, [status]);

  useEffect(() => () => removePaylineWidget(), []);

  const capture = (data, creditCard) => {
    // Ne persiste que si une carte est réellement créée ci-dessous, pour être
    // reporté dans la transaction (sinon credit_card_id resterait NULL).
    data.newCreditCardId = null;

    if (creditCard.saveImprint) {
      const uuid = helpers.uuid.createUuid();
      data.newCreditCardId = uuid;

      return Payline.api
        .subscribeAndCapture(apolloClient, {
          uuid,
          id3D: data.id3D,
          amount: data.amount,
          reference: account.id,
          creditCard: helpers.object.filterByKeys(creditCard, ['number', 'dueDate', 'cvv']),
        })
        .catch((err) => {
          throw Object.assign(err, { onSubscribe: true });
        })
        .then((res) =>
          CreditCard.api
            .setCreditCard(apolloClient, {
              id: uuid,
              token: res.token,
              number: CreditCard.functions.anonymizeNumber(creditCard.number),
              ...helpers.object.filterByKeys(creditCard, ['name', 'dueDate', 'country', 'brand', 'type']),
            })
            .then(() => res),
        )
        .catch((err) => {
          if (!err.onSubscribe) {
            throw Object.assign(err, {
              message: 'Impossible d’enregistrer la carte. Veuillez réessayer ultérieurement.',
            });
          }
          throw err;
        });
    }

    return Payline.api.capture(apolloClient, {
      id3D: data.id3D,
      reference: data.id,
      amount: data.amount,
      creditCard: helpers.object.filterByKeys(creditCard, ['number', 'dueDate', 'cvv']),
    });
  };

  /*
   * Échec de `bookingConfirm` APRÈS un encaissement réussi.
   *
   * L'ancien traitement était `if (!err.handled) return data;` : la chaîne
   * continuait comme si de rien n'était et poussait l'utilisateur sur l'écran de
   * confirmation, alors que la réservation n'avait jamais été confirmée côté
   * serveur. Argent pris, activité non réservée, et personne au courant.
   *
   * On tente donc la reprise serveur `bookingConfirmIfCaptured` (idempotent : il
   * confirme à partir du paiement déjà encaissé, sans recapturer), exactement comme
   * le fait le tunnel Booking du web. Si le serveur ne confirme toujours pas, on le
   * dit franchement plutôt que d'afficher une fausse confirmation.
   */
  const recoverAfterCapture = (data) => async (err) => {
    if (err.handled) {
      setError(err.message);
      setWaiting(false);
      throw err;
    }

    /*
     * Y a-t-il de l'argent en jeu ?
     *
     * Deux façons de le savoir, et il faut les DEUX. Sur les paiements par carte
     * saisie ou en un clic, c'est l'obtention d'une transaction Monext. Sur le
     * chemin widget, c'est le seul fait que le widget ait signalé un succès :
     * l'événement arrive après le débit, et tout ce qui le suit peut échouer avant
     * qu'une transaction ait pu être construite.
     *
     * Un `false` ici saute la reprise ET affiche un message d'échec, donc invite à
     * repayer. Sur ce tunnel, `activityCancel` ne rembourse pas : un second débit
     * ne se rattrape pas tout seul. En cas de doute, cette variable doit valoir
     * `true`.
     */
    const encaisse = Boolean(data.widgetCaptured || (data.transaction && data.transaction.transactionNo));

    if (data.id && encaisse) {
      setStatus('Vérification du paiement...');
      const recovery = await Booking.api
        .bookingConfirmIfCaptured(apolloClient, { id: data.id })
        .catch(() => null);

      /*
       * `paid: true` ne veut PAS dire « réservation confirmée ». Le serveur s'en sert
       * aussi pour dire « ne surtout pas annuler » sur ses abstentions (verrou
       * contesté, capture encore en vol), et il répond alors `status: 'pending'` sans
       * transaction. Sur cette réponse-là, afficher l'écran de confirmation ferait
       * croire au golfeur que son activité est réservée alors qu'elle ne l'est pas.
       */
      if (recovery && recovery.paid && recovery.status === 'confirmed') {
        return { transaction: recovery.transaction || data.transaction };
      }
    }

    /*
     * Le message ne dit PAS « votre paiement a été accepté » : on ne le sait pas
     * toujours. Il ne dit pas non plus « réessayez » : ce serait le pire des
     * conseils si le débit est passé. Et il ne promet AUCUN rappel : quand rien n'a
     * pu être enregistré, le golf ne voit strictement rien de ce paiement, et
     * annoncer qu'on le recontacte le laisserait attendre un appel qui ne viendrait
     * jamais. Il ne reste qu'une action fiable, et c'est celle qu'on lui donne.
     */
    setError(
      encaisse
        ? 'Nous n’avons pas pu confirmer votre réservation. Ne renouvelez pas l’opération : '
          + 'contactez l’accueil du golf, qui vérifiera si votre paiement est passé.'
        : err.message || 'Le paiement n’a pas abouti.',
    );
    setWaiting(false);
    setStatus(null);
    throw Object.assign(err, { handled: true });
  };

  const payWithCardImprint = () => {
    // Garde-fou (défense en profondeur) : ne jamais tenter un paiement 1-clic avec une
    // carte dépourvue de wallet Payline réel. Normalement déjà filtré au chargement.
    if (!imprint || !imprint.walletId) {
      setError(
        "Cette carte enregistrée n'est plus utilisable pour le paiement en 1 clic. Réglez avec une nouvelle carte : elle sera ré-enregistrée automatiquement.",
      );
      return Promise.resolve();
    }

    const subscriber = {
      id: imprint.walletId,
      token: imprint.token,
      dueDate: imprint.dueDate,
    };

    const data = {};

    setError(null);
    setWaiting(true);
    setStatus('Nous prenons votre réservation au golf...');

    return Activity.api
      .activityBook(masterClient, {
        ...location.state,
        golf,
        accountId: account.id,
        players: location.state.players.map((player, index) => {
          if (index === 0) {
            return {
              ...account,
              ...player,
            };
          }
          return {
            ...player,
          };
        }),
      })
      .catch((err) => {
        throw Object.assign(err, { handled: true });
      })
      .then((res) => {
        data.id = res.id;
        data.amount = res.total.price;
        setStatus('Nous interrogeons votre banque...');

        const captureApi = Payline.api.captureSubscriber;

        return captureApi(apolloClient, {
          amount: data.amount,
          reference: data.id,
          subscriber,
        }).then((res) =>
          // Sur challenge 3DS (01131), le serveur répond `{ needs3DS: true }` SANS
          // transactionId : rien n'a été encaissé. La garde est placée ICI, avant le
          // `catch` suivant, pour que le créneau provisoire soit bien libéré comme
          // pour n'importe quel refus. Sans elle, l'activité était confirmée
          // gratuitement sur toute carte soumise à SCA.
          assertCaptured(res, 'Activity.captureSubscriber'),
        );
      })
      .catch((err) => {
        if (!err.handled) {
          // Aucun débit n'a eu lieu : on ne rembourse rien, on libère le créneau.
          setError('Paiement non abouti, libération du créneau...');
          // `bookingCancel` envoie au golf l'action PGB 35, celle des green-fees : elle ne
          // libère JAMAIS un créneau d'activité. `activityCancel` envoie la 153, la bonne.
          // Via masterClient, comme l'écran Historique : primapp-api ne déclare pas cette
          // mutation dans son propre schéma.
          return Activity.api
            .activityCancel(masterClient, { id: data.id, failed: true })
            .then(() => {
              throw Object.assign(err, { handled: true });
            })
            .catch(() => {
              throw Object.assign(err, { handled: true });
            });
        }
        throw err;
      })
      .then((res) => {
        data.transaction = {
          amount: data.amount,
          card: imprint.number,
          creditCardId: imprint.id,
          ...buildTransactionFields(res),
          ...helpers.object.filterByKeys(imprint, ['type', 'brand', 'country']),
        };

        return Booking.api.bookingConfirm(apolloClient, {
          id: data.id,
          transaction: data.transaction,
        });
      })
      .catch(recoverAfterCapture(data))
      .then(({ transaction }) => {
        history.push({
          pathname: '/activity/confirmation',
          state: {
            ...location.state,
            ...data,
            transaction,
          },
        });
      })
      .catch((err) => {
        if (!err.handled) {
          setError(err.message);
          setWaiting(false);
        }
      });
  };

  const payWithNewCard = (values, { setSubmitting }) => {
    const { id3D = null } = values;

    const creditCard = {
      ...values,
      name: '3DS web',
      number: values.number.replace(/\s/g, ''),
      dueDate: values.dueDate.replace(/\D/g, ''),
    };

    const data = { id3D };

    setError(null);
    setWaiting(true);
    setStatus('Nous prenons votre réservation au golf...');
    return Activity.api
      .activityBook(masterClient, {
        ...location.state,
        golf,
        accountId: account.id,
        players: location.state.players.map((player, index) => {
          if (index === 0) {
            return {
              ...account,
              ...player,
            };
          }
          return {
            ...player,
          };
        }),
      })
      .catch((err) => {
        throw Object.assign(err, { handled: true });
      })
      .then((res) => {
        data.id = res.id;
        data.amount = res.total.price;
        setStatus('Nous interrogeons votre banque...');

        return capture(data, creditCard).then((res) => assertCaptured(res, 'Activity.captureNewCard'));
      })
      .catch((err) => {
        if (!err.handled) {
          // Aucun débit n'a eu lieu : on ne rembourse rien, on libère le créneau.
          setStatus('Paiement non abouti, libération du créneau...');
          setWaiting(false);

          // `bookingCancel` envoie au golf l'action PGB 35, celle des green-fees : elle ne
          // libère JAMAIS un créneau d'activité. `activityCancel` envoie la 153, la bonne.
          // Via masterClient, comme l'écran Historique : primapp-api ne déclare pas cette
          // mutation dans son propre schéma.
          return Activity.api
            .activityCancel(masterClient, { id: data.id, failed: true })
            .then(() => {
              throw Object.assign(err, { handled: true });
            })
            .catch(() => {
              throw Object.assign(err, { handled: true });
            });
        }
        throw err;
      })
      .then((res) => {
        data.transaction = {
          amount: data.amount,
          card: CreditCard.functions.anonymizeNumber(creditCard.number),
          // Rattache le paiement à la carte enregistrée à la volée (null sinon).
          creditCardId: data.newCreditCardId || null,
          ...buildTransactionFields(res),
          ...helpers.object.filterByKeys(creditCard, ['type', 'brand', 'country']),
        };

        return Booking.api.bookingConfirm(apolloClient, {
          id: data.id,
          transaction: data.transaction,
        });
      })
      .catch(recoverAfterCapture(data))
      .then(({ transaction }) => {
        history.push({
          pathname: '/activity/confirmation',
          state: {
            ...location.state,
            ...data,
            transaction,
          },
        });
      })
      .catch((err) => {
        if (!err.handled) {
          setStatus(err.message);
          setWaiting(false);
        }
        setSubmitting(false);
      });
  };

  const payWithNewCardPayline = () => {
    const data = {};
    const saveImprint = sessionStorage.getItem('payline_saveImprint') === '1';

    setError(null);
    setWaiting(true);
    setStatus('Nous prenons votre réservation au golf...');

    return Activity.api
      .activityBook(masterClient, {
        ...location.state,
        golf,
        accountId: account.id,
        players: location.state.players.map((player, index) => {
          if (index === 0) {
            return {
              ...account,
              ...player,
            };
          }
          return {
            ...player,
          };
        }),
      })
      .catch((err) => {
        throw Object.assign(err, { handled: true });
      })
      .then((res) => {
        data.id = res.id;
        data.amount = res.total.price;
        setStatus('Nous préparons le paiement...');

        const baseUrl = window.location.origin;
        const timestamp = Number(new Date());
        const shortId = data.id.split('-')[0];

        return Payline.api.getToken(apolloClient, {
          amount: data.amount,
          reference: `ACT-${shortId}-${timestamp}`,
          returnUrl: [baseUrl, '/activity/confirmation'].join(''),
          cancelUrl: [baseUrl, '/activity/payment'].join(''),
          callbackUrl: [baseUrl, '/payline/callback'].join(''),
          customer: {
            firstName: account.firstname,
            lastName: account.lastname,
            email: account.email,
            phone: account.phone,
          },
        });
      })
      .then((res) => {
        if (!res?.token) {
          throw new Error('Token de paiement introuvable.');
        }

        data.token = res.token;
        data.locationState = location.state;
        paylineWidgetData.current = data;
        isPaylinePaymentHandled.current = false;

        setWaiting(false);
        setStatus(null);

        return loadPaylineScript().then(() => {
          showPaylineWidget(data.token);
        });
      })
      .catch((err) => {
        if (!err.handled && data.id) {
          // Échec avant l'ouverture du widget : rien n'a été encaissé, on libère
          // seulement le créneau provisoire.
          setStatus('Paiement non abouti, libération du créneau...');

          // `bookingCancel` envoie au golf l'action PGB 35, celle des green-fees : elle ne
          // libère JAMAIS un créneau d'activité. `activityCancel` envoie la 153, la bonne.
          // Via masterClient, comme l'écran Historique : primapp-api ne déclare pas cette
          // mutation dans son propre schéma.
          return Activity.api
            .activityCancel(masterClient, { id: data.id, failed: true })
            .then(() => {
              throw Object.assign(err, { handled: true });
            })
            .catch(() => {
              throw Object.assign(err, { handled: true });
            });
        }

        throw err;
      })
      .catch((err) => {
        setError(err.message);
        setWaiting(false);
        setStatus(null);
        paylineWidgetData.current = null;
        removePaylineWidget();
      });
  };

  const session3DS = {
    address: {
      city: golf.city,
      countryCode: 250, // golf.country
      line1: golf.name,
      zipCode: golf.postalCode,
    },
    customer: {
      email: account.email,
      lastName: account.lastname,
      firstName: account.firstname,
      phone: account.phone,
    },
  };

  const numberOfPlayers = players?.length || 0;

  const dateTxt = helpers.string.ucfirst(i18n.l('date.formats.long_y', new Date(bookingInfo.startsAt)));
  const courseName = bookingInfo.resourceName;
  const detailsTxt = `${i18n.t('terms.players', { count: numberOfPlayers })}`;

  return (
    <Screen
      noDrawer={isPublic}
      layout={'fluid-large'}
      ForwardIcon={ForwardIcon}
      helpURL={`${HELP_URL}/r%C3%A9server`}
      onRetry={load}
      title='Paiement par CB'
      error={error}
    >
      <Typography align='center' component='h2' variant='h5'>
        {`${dateTxt} • ${selectedSlot}`}
      </Typography>
      <Typography align='center' component='p' color='textSecondary' variant='h6'>
        {courseName}
      </Typography>
      <Typography align='center' component='p' variant='body1'>
        {detailsTxt}
      </Typography>
      <br />
      <Payment
        amount={bookingInfo.price}
        imprint={imprint}
        currency={bookingInfo.currency || 'EUR'}
        session3DS={session3DS}
        isPaylineProvider={PaymentProvider.isPayline(golf)}
        payWithNewCard={payWithNewCard}
        payWithNewCardPayline={payWithNewCardPayline}
        payWithCardImprint={payWithCardImprint}
      />
      <div id='PaylineWidget' />
      <DialogPaymentIP open={waiting} status={status} />
    </Screen>
  );
};

const mapStateToProps = ({ app, bookingData }) => ({
  golf: app.golf,
  data: bookingData,
  account: app.account,
});

const StyledComponent = withStyles(styles)(ActivityPayment);

export default connect(mapStateToProps, Booking.actions)(StyledComponent);
